import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiPlus, FiEdit2, FiTrash2, FiBell,
    FiCheckCircle, FiXCircle, FiFilter,
    FiAlertTriangle, FiZap, FiInfo, FiSearch
} from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';
import toast from 'react-hot-toast';

const AdminNotices = () => {
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingNotice, setEditingNotice] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        priority: 'medium',
        is_active: 1
    });

    useEffect(() => {
        loadNotices();
    }, []);

    const loadNotices = async () => {
        try {
            const data = await fetchAPI('/admin/get_notices.php');
            setNotices(data);
        } catch (error) {
            toast.error('Failed to load notices');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (notice = null) => {
        if (notice) {
            setEditingNotice(notice);
            setFormData({
                title: notice.title,
                content: notice.content,
                priority: notice.priority,
                is_active: notice.is_active
            });
        } else {
            setEditingNotice(null);
            setFormData({
                title: '',
                content: '',
                priority: 'medium',
                is_active: 1
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = editingNotice ? { ...formData, id: editingNotice.id } : formData;
            await fetchAPI('/admin/save_notice.php', {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            toast.success(editingNotice ? 'Notice updated' : 'Notice created');
            setIsModalOpen(false);
            loadNotices();
        } catch (error) {
            toast.error('Error saving notice');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this notice?')) return;
        try {
            await fetchAPI('/admin/delete_notice.php', {
                method: 'POST',
                body: JSON.stringify({ id })
            });
            toast.success('Notice deleted');
            loadNotices();
        } catch (error) {
            toast.error('Error deleting notice');
        }
    };

    const filteredNotices = notices.filter(n =>
        n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.content.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgent': return 'text-red-500 bg-red-500/10 border-red-500/20';
            case 'high': return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
            case 'medium': return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
            default: return 'text-neutral-400 bg-neutral-400/10 border-neutral-400/20';
        }
    };

    return (
        <div className="min-h-screen bg-[#F4F5F7] p-8">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-blue-600 rounded-lg shadow-lg">
                            <FiBell className="text-white" />
                        </div>
                        <h1 className="text-3xl font-black text-gray-900 italic uppercase tracking-tight">
                            Notice <span className="text-blue-600">Center</span>
                        </h1>
                    </div>
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-[0.3em]">Administrator Command Console // Protocols</p>
                </div>

                <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className="relative flex-1 md:w-64">
                        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="SEARCH REGISTRY..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-12 pr-4 text-xs font-bold text-gray-900 uppercase tracking-widest focus:border-blue-500/50 outline-none transition-all"
                        />
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-xl flex items-center gap-2 font-black uppercase tracking-tighter transition-all shadow-lg"
                    >
                        <FiPlus /> <span className="hidden md:inline">Generate Notice</span>
                    </button>
                </div>
            </div>

            {/* Content Table/Grid */}
            {loading ? (
                <div className="h-64 flex flex-col items-center justify-center gap-4 opacity-50">
                    <div className="w-8 h-8 border-2 border-blue-500/20 border-t-blue-600 rounded-full animate-spin"></div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-900">Accessing Secure Registry...</p>
                </div>
            ) : filteredNotices.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredNotices.map((notice) => (
                        <motion.div
                            key={notice.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white border border-gray-100 rounded-3xl p-6 hover:shadow-xl hover:border-blue-500/30 transition-all group relative overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                <FiBell size={40} className="text-gray-900" />
                            </div>

                            <div className="flex justify-between items-start mb-4 relative z-10">
                                <div className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${getPriorityColor(notice.priority)}`}>
                                    {notice.priority}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleOpenModal(notice)}
                                        className="p-2 bg-gray-50 hover:bg-blue-50 text-gray-400 hover:text-blue-600 rounded-lg transition-all"
                                    >
                                        <FiEdit2 size={14} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(notice.id)}
                                        className="p-2 bg-gray-50 hover:bg-red-50 text-gray-400 hover:text-red-600 rounded-lg transition-all"
                                    >
                                        <FiTrash2 size={14} />
                                    </button>
                                </div>
                            </div>

                            <h3 className="text-lg font-black text-gray-900 mb-2 uppercase italic tracking-tight group-hover:text-blue-600 transition-colors">
                                {notice.title}
                            </h3>
                            <p className="text-sm text-gray-500 leading-relaxed line-clamp-3 mb-6 font-medium">
                                {notice.content}
                            </p>

                            <div className="pt-6 border-t border-gray-50 flex justify-between items-center">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                    {new Date(notice.created_at).toLocaleDateString()}
                                </span>
                                <div className="flex items-center gap-2">
                                    <span className={`text-[8px] font-black uppercase tracking-widest ${notice.is_active ? 'text-green-600' : 'text-red-600'}`}>
                                        {notice.is_active ? 'Status: Active' : 'Status: Inactive'}
                                    </span>
                                    {notice.is_active ? <FiCheckCircle className="text-green-600" /> : <FiXCircle className="text-red-600" />}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            ) : (
                <div className="h-64 border-2 border-dashed border-gray-200 rounded-[3rem] flex flex-col items-center justify-center gap-4 opacity-30">
                    <FiBell size={48} className="text-gray-900" />
                    <p className="text-xs font-black uppercase tracking-[0.3em] text-gray-900">No notices found in central hub</p>
                </div>
            )}

            {/* Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="fixed inset-0 z-[3000] flex items-center justify-center p-6">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsModalOpen(false)}
                            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        ></motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="relative w-full max-w-xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden"
                        >
                            <div className="p-8 md:p-12">
                                <h2 className="text-2xl font-black text-gray-900 italic uppercase tracking-tight mb-8">
                                    {editingNotice ? 'Update' : 'Generate'} <span className="text-blue-600">Notice Protocol</span>
                                </h2>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div>
                                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-2">Subject Header</label>
                                        <input
                                            required
                                            type="text"
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            placeholder="e.g. URGENT: CLASS RESCHEDULE"
                                            className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-gray-900 font-bold tracking-tight focus:border-blue-500/50 outline-none transition-all placeholder:text-gray-300"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-2">Content Payload</label>
                                        <textarea
                                            required
                                            rows={4}
                                            value={formData.content}
                                            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                            placeholder="Enter detailed notice information..."
                                            className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-gray-900 font-medium tracking-tight focus:border-blue-500/50 outline-none transition-all placeholder:text-gray-300 resize-none"
                                        ></textarea>
                                    </div>

                                    <div className="grid grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-2">Priority Level</label>
                                            <select
                                                value={formData.priority}
                                                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                                className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-gray-900 font-bold tracking-tight focus:border-blue-500/50 outline-none transition-all"
                                            >
                                                <option value="low">LOW</option>
                                                <option value="medium">MEDIUM</option>
                                                <option value="high">HIGH</option>
                                                <option value="urgent">URGENT</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-2">Activity Status</label>
                                            <select
                                                value={formData.is_active}
                                                onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                                                className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-gray-900 font-bold tracking-tight focus:border-blue-500/50 outline-none transition-all"
                                            >
                                                <option value={1}>ACTIVE</option>
                                                <option value={0}>INACTIVE</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="flex gap-4 pt-6">
                                        <button
                                            type="button"
                                            onClick={() => setIsModalOpen(false)}
                                            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-900 font-black uppercase tracking-widest py-4 rounded-2xl transition-all"
                                        >
                                            Abort
                                        </button>
                                        <button
                                            type="submit"
                                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest py-4 rounded-2xl transition-all shadow-lg shadow-blue-600/20"
                                        >
                                            Execute Protocol
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminNotices;
